// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — App Shell
// Layout + view routing + search overlay + toast notifications
// ═══════════════════════════════════════════════════════════════════════════════
import { lazy, Suspense, useEffect, useCallback, useRef, useMemo, type ComponentType } from 'react';
import { AppProvider, useApp } from './hooks/useAppState';
import { AuthGuard } from './components/AuthGuard';
import { ErrorBoundary } from './components/ErrorBoundary';
import { editorStyles, articleContentStyles } from './components/kb/RichTextEditor';
import { Sidebar } from './components/layout/Sidebar';
import { TopBar } from './components/layout/TopBar';
import { SearchOverlay } from './components/layout/SearchOverlay';
import { Toast } from './components/layout/Toast';
import { TaskDrawer } from './components/shared/TaskDrawer';

// Wraps React.lazy with a one-shot reload guard for stale-chunk errors.
// After a new deploy, content-hashed chunk filenames change. If a user has the
// old index.html open, their dynamic imports 404. On the first such error we
// reload to pick up the new index.html (and new chunk filenames). On a second
// consecutive failure the error propagates to the ErrorBoundary so the user
// sees the error card rather than an infinite reload loop.
import { CHUNK_RELOAD_KEY } from './config/constants';
import { isViewAllowed, isGmView, isKnownUserRole, isKnownView } from './config/featureVisibility';
function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const e = err as Error;
      const isChunkError =
        e?.name === 'ChunkLoadError' ||
        /failed to fetch dynamically imported module|loading chunk/i.test(e?.message ?? '');
      if (isChunkError && sessionStorage.getItem(CHUNK_RELOAD_KEY) !== '1') {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
        return new Promise<{ default: T }>(() => {}); // never resolves; reload takes over
      }
      throw err;
    }),
  );
}

// Route-level code splitting — each page loads only when first navigated to
const HomePage            = lazyWithRetry(() => import('./pages/HomePage').then(m => ({ default: m.HomePage })));
const ArticlePage         = lazyWithRetry(() => import('./pages/ArticlePage').then(m => ({ default: m.ArticlePage })));
const EditorPage          = lazyWithRetry(() => import('./pages/EditorPage').then(m => ({ default: m.EditorPage })));
const KqlLibraryPage      = lazyWithRetry(() => import('./pages/KqlLibraryPage').then(m => ({ default: m.KqlLibraryPage })));
const VerifyPage          = lazyWithRetry(() => import('./pages/VerifyPage').then(m => ({ default: m.VerifyPage })));
const MitrePage           = lazyWithRetry(() => import('./pages/MitrePage').then(m => ({ default: m.MitrePage })));
const DecisionsPage       = lazyWithRetry(() => import('./pages/DecisionsPage').then(m => ({ default: m.DecisionsPage })));
const AIAssistantPage     = lazyWithRetry(() => import('./pages/AIAssistantPage').then(m => ({ default: m.AIAssistantPage })));
const HandoffPage         = lazyWithRetry(() => import('./pages/HandoffPage').then(m => ({ default: m.HandoffPage })));
const CustomersPage       = lazyWithRetry(() => import('./pages/CustomersPage').then(m => ({ default: m.CustomersPage })));
const OnboardingPage      = lazyWithRetry(() => import('./pages/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const GlossaryPage        = lazyWithRetry(() => import('./pages/GlossaryPage').then(m => ({ default: m.GlossaryPage })));
const LMSPage             = lazyWithRetry(() => import('./pages/LMSPage').then(m => ({ default: m.LMSPage })));
const ProjectsPage        = lazyWithRetry(() => import('./pages/ProjectsPage').then(m => ({ default: m.ProjectsPage })));
const TasksPage           = lazyWithRetry(() => import('./pages/TasksPage').then(m => ({ default: m.TasksPage })));
const CalendarPage        = lazyWithRetry(() => import('./pages/CalendarPage').then(m => ({ default: m.CalendarPage })));
const WorkloadPage        = lazyWithRetry(() => import('./pages/WorkloadPage').then(m => ({ default: m.WorkloadPage })));
const AdminPage           = lazyWithRetry(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const SLADashboardPage    = lazyWithRetry(() => import('./pages/SLADashboardPage').then(m => ({ default: m.SLADashboardPage })));
const EscalationPage      = lazyWithRetry(() => import('./pages/EscalationPage').then(m => ({ default: m.EscalationPage })));
const IncidentResponsePage = lazyWithRetry(() => import('./pages/IncidentResponsePage').then(m => ({ default: m.IncidentResponsePage })));
const ThreatHuntingPage   = lazyWithRetry(() => import('./pages/ThreatHuntingPage').then(m => ({ default: m.ThreatHuntingPage })));
const EngineeringPage     = lazyWithRetry(() => import('./pages/EngineeringPage').then(m => ({ default: m.EngineeringPage })));
const ProvisioningPage    = lazyWithRetry(() => import('./pages/ProvisioningPage').then(m => ({ default: m.ProvisioningPage })));
const ShiftRosterPage     = lazyWithRetry(() => import('./pages/ShiftRosterPage').then(m => ({ default: m.ShiftRosterPage })));
const RosterManagementPage = lazyWithRetry(() => import('./pages/RosterManagementPage').then(m => ({ default: m.RosterManagementPage })));
const CoveragePage        = lazyWithRetry(() => import('./pages/CoveragePage').then(m => ({ default: m.CoveragePage })));
const ChecklistPage       = lazyWithRetry(() => import('./pages/ChecklistPage').then(m => ({ default: m.ChecklistPage })));
const QAScoresPage        = lazyWithRetry(() => import('./pages/QAScoresPage').then(m => ({ default: m.QAScoresPage })));
const QaAdminPage         = lazyWithRetry(() => import('./pages/QaAdminPage').then(m => ({ default: m.QaAdminPage })));
const ReportsPage         = lazyWithRetry(() => import('./pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const FreshServicePage    = lazyWithRetry(() => import('./pages/FreshServicePage').then(m => ({ default: m.FreshServicePage })));
const CustomerPortalPage  = lazyWithRetry(() => import('./pages/CustomerPortalPage').then(m => ({ default: m.CustomerPortalPage })));
const KBPage              = lazyWithRetry(() => import('./pages/KBPage').then(m => ({ default: m.KBPage })));
const SignInLogPage        = lazyWithRetry(() => import('./pages/SignInLogPage').then(m => ({ default: m.SignInLogPage })));
const ApiKeysPage         = lazyWithRetry(() => import('./pages/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));
const MyFeatureRequestsPage = lazyWithRetry(() => import('./pages/MyFeatureRequestsPage').then(m => ({ default: m.MyFeatureRequestsPage })));
const MyEngineeringRequestsPage = lazyWithRetry(() => import('./pages/MyEngineeringRequestsPage').then(m => ({ default: m.MyEngineeringRequestsPage })));
// General Management (RMS→Hub integration — Phase 1 scaffold)
const GMPage              = lazyWithRetry(() => import('./pages/GMPage').then(m => ({ default: m.GMPage })));
const TractionPage        = lazyWithRetry(() => import('./pages/TractionPage').then(m => ({ default: m.TractionPage })));
const HiringPage          = lazyWithRetry(() => import('./pages/HiringPage').then(m => ({ default: m.HiringPage })));
const PeoplePage          = lazyWithRetry(() => import('./pages/PeoplePage').then(m => ({ default: m.PeoplePage })));
const DisciplinePage      = lazyWithRetry(() => import('./pages/DisciplinePage').then(m => ({ default: m.DisciplinePage })));
const ExecReportsPage     = lazyWithRetry(() => import('./pages/ExecReportsPage').then(m => ({ default: m.ExecReportsPage })));

// Global FAB — mounted in AppLayout so it's reachable from every route.
// Internally flag-gated; renders null when FEATURE_ANALYST_ENGINEERING_FAB is off.
import { AnalystRequestFab } from './components/engineering/AnalystRequestFab';
import { apiFetch } from './services/apiFetch';

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* SW optional */ });
  });
}

// KB editor/article CSS must rebuild when the theme flips — the strings read
// the live theme object, and useApp() re-renders this on theme change.
function GlobalContentStyles() {
  const { currentTheme } = useApp();
  const css = useMemo(() => editorStyles() + articleContentStyles(), [currentTheme]);
  return <style dangerouslySetInnerHTML={{ __html: css }} />;
}

export function App() {
  return (
    <ErrorBoundary fallbackTitle="The app failed to load. Reload the page or contact an admin.">
      <AppProvider>
        <GlobalContentStyles />
        <AuthGuard>
          <AppLayout />
        </AuthGuard>
      </AppProvider>
    </ErrorBoundary>
  );
}

function ImpersonationBanner() {
  const { state, dispatch, isImpersonating } = useApp();
  if (!isImpersonating) return null;
  const originalUser = state.originalUser;
  return (
    <div style={{
      background: 'linear-gradient(90deg, #f59e0b, #d97706)',
      color: '#000',
      padding: '6px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: 12,
      fontWeight: 600,
      flexShrink: 0,
    }}>
      <span>
        Impersonating <strong>{state.user.name}</strong> ({state.user.role} / {state.user.tier} / {state.user.shift})
        {originalUser && <span style={{ fontWeight: 400 }}> — signed in as {originalUser.name}</span>}
      </span>
      <button
        onClick={() => dispatch({ type: 'STOP_IMPERSONATION' })}
        style={{
          background: '#000',
          color: '#f59e0b',
          border: 'none',
          borderRadius: 4,
          padding: '3px 10px',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        End Impersonation
      </button>
    </div>
  );
}

function PageSpinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
      <div style={{
        width: 32, height: 32,
        border: '3px solid var(--border, #333)',
        borderTopColor: 'var(--accent, #3b82f6)',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function AppLayout() {
  const { state, dispatch, currentTheme, canViewView } = useApp();
  const mainRef = useRef<HTMLElement>(null);

  // Build a unique key representing the current "position" (view + sub-tab)
  const scrollKey = useMemo(() => {
    let key = `mxdr365_scroll_${state.view}`;
    if (state.view === 'admin' || state.view === 'users') key += `_${state.adminTab}`;
    else if (state.view === 'freshservice' || state.view === 'tickets' || state.view === 'ticket') key += `_${state.freshServiceTab}`;
    else if (state.view === 'lms') key += `_${state.lmsTab}`;
    else if (state.view === 'projects') key += `_${state.pmMode}`;
    return key;
  }, [state.view, state.adminTab, state.freshServiceTab, state.lmsTab, state.pmMode]);

  // Save scroll position to sessionStorage (debounced)
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout>;
    const onScroll = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        sessionStorage.setItem(scrollKey, String(el.scrollTop));
      }, 150);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => { el.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, [scrollKey]);

  // Restore scroll position when scrollKey changes (view or tab change)
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const saved = sessionStorage.getItem(scrollKey);
    // rAF ensures the new view has rendered before we scroll
    const raf = requestAnimationFrame(() => {
      el.scrollTop = saved !== null ? parseInt(saved, 10) : 0;
    });
    return () => cancelAnimationFrame(raf);
  }, [scrollKey]);

  // Apply theme to CSS variables on body
  useEffect(() => {
    const t = currentTheme;
    const root = document.documentElement;
    root.style.setProperty('--bg', t.bg);
    root.style.setProperty('--text', t.text);
    root.style.setProperty('--border', t.border);
    root.style.setProperty('--accent', t.accent);
    root.style.setProperty('--scrollbar', t.scrollbar);
    root.style.setProperty('--scrollbar-hover', t.scrollbarHover);
    root.style.setProperty('--selection', t.selection);
    root.style.setProperty('--focus-ring', t.focusRing);
    // Tells the browser to render native chrome (date pickers, selects,
    // checkboxes, autofill) in the matching light/dark style.
    root.style.colorScheme = t.mode;
    document.body.style.background = t.bg;
    document.body.style.color = t.text;
  }, [currentTheme]);

  // Push notification subscription — request permission and register with backend on first visit
  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (Notification.permission === 'denied') return;

    async function subscribeToPush() {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        if (existing) return; // already subscribed

        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        const vapidKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
        if (!vapidKey) return; // VAPID key not configured — skip

        const sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidKey,
        });

        await apiFetch('/api/notifications/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sub.toJSON()),
        });
      } catch {
        // Permission denied or push not supported — silently ignore
      }
    }

    subscribeToPush();
  }, []);

  // Clear the stale-chunk reload guard after a successful route render so that
  // the next navigation after a future deploy can trigger a fresh silent reload.
  useEffect(() => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  }, [state.view]);

  // Keyboard shortcuts. Search indexes internal content (articles, runbooks,
  // glossary) — never offered to customer-role users.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (state.user.role === 'customer') return;
        dispatch({ type: 'SET_SEARCH_OPEN', open: !state.searchOpen });
      }
      if (e.key === 'Escape') {
        dispatch({ type: 'SET_SEARCH_OPEN', open: false });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [state.searchOpen, state.user.role, dispatch]);

  // Customer-role users are HARD-PINNED to the portal view. This is a
  // render-time guarantee (not just nav filtering): a persisted localStorage
  // view, hash manipulation, or stale SET_VIEW can never mount an internal
  // page for a customer account.
  const isCustomer = state.user.role === 'customer';

  // The SAME render-time guarantee for STAFF roles.
  //
  // The customer pin above has always been render-time for exactly the reason
  // written on it - a persisted localStorage view must not be able to mount a
  // page. Staff roles never got the equivalent, so a stale mxdr365_view (role
  // change, shared machine, someone else's session) mounted whatever it said:
  // an analyst landed on the Engineering blade, complete with the queue.
  //
  // useAppState re-validates and bounces home once /api/me lands; this stops the
  // page painting at all in the meantime, and it holds even if that effect is
  // ever refactored away. Waits for a real id because BLANK_USER looks like an
  // analyst until SET_CURRENT_USER arrives.
  const viewPermitted = !state.user.id
    || (isKnownUserRole(state.user.role) && isKnownView(state.view) && (
      isViewAllowed(state.user.role, state.view)
      || canViewView(state.view)
      || (state.user.tier === 'GM' && isGmView(state.view))
    ));

  // Keep state.view in sync for customers so the sidebar item highlights and
  // the URL hash reflects the only view they have.
  useEffect(() => {
    if (isCustomer && state.view !== 'customerPortal') {
      dispatch({ type: 'SET_VIEW', view: 'customerPortal' });
    }
  }, [isCustomer, state.view, dispatch]);

  const renderView = useCallback(() => {
    if (isCustomer) return <CustomerPortalPage />;
    // Fail closed: Home rather than the requested page. Home is in every staff
    // role's visible set, so this cannot loop.
    if (!viewPermitted) return <HomePage />;
    switch (state.view) {
      // (state.view cases below are unreachable for customer-role users)
      case 'home': return <HomePage />;
      case 'kb': return <KBPage />;
      case 'article': return <ArticlePage />;
      case 'editor': return <EditorPage />;
      case 'kql': return <KqlLibraryPage />;
      case 'verify': return <VerifyPage />;
      case 'mitre': return <MitrePage />;
      case 'decisions': return <DecisionsPage />;
      case 'ai': return <AIAssistantPage />;
      case 'handoff': return <HandoffPage />;
      case 'customers': return <CustomersPage />;
      case 'onboarding': return <OnboardingPage />;
      case 'glossary': return <GlossaryPage />;
      case 'lms': return <LMSPage />;
      case 'projects': return <ProjectsPage />;
      case 'tasks':
      case 'mytasks':
        return <TasksPage />;
      case 'calendar': return <CalendarPage />;
      case 'workload': return <WorkloadPage />;
      case 'reports': return <ReportsPage />;
      case 'freshservice':
      case 'tickets':
      case 'ticket':
        return <FreshServicePage />;
      case 'customerPortal': return <CustomerPortalPage />;
      case 'admin':
      case 'users':
        return <AdminPage />;
      case 'signin-log': return <SignInLogPage />;
      case 'api_keys':   return <ApiKeysPage />;
      case 'sla':         return <SLADashboardPage />;
      case 'escalations': return <EscalationPage />;
      case 'ir':          return <IncidentResponsePage />;
      case 'threatHunting': return <ThreatHuntingPage />;
      case 'engineering': return <EngineeringPage />;
      case 'provisioning': return <ProvisioningPage />;
      case 'roster':      return <ShiftRosterPage />;
      case 'roster-mgmt': return <RosterManagementPage />;
      case 'coverage':    return <CoveragePage />;
      case 'checklist':   return <ChecklistPage />;
      case 'qa':          return <QAScoresPage />;
      case 'qa_admin':    return <QaAdminPage />;
      case 'my-feature-requests': return <MyFeatureRequestsPage />;
      case 'my-engineering-requests': return <MyEngineeringRequestsPage />;
      case 'gm':           return <GMPage />;
      case 'traction':     return <TractionPage />;
      case 'hiring':       return <HiringPage />;
      case 'people':       return <PeoplePage />;
      case 'discipline':   return <DisciplinePage />;
      case 'exec-reports': return <ExecReportsPage />;
      default: return <HomePage />;
    }
  }, [state.view, isCustomer, viewPermitted]);

  const t = currentTheme;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: t.bg }}>
      <Sidebar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ImpersonationBanner />
        <TopBar />
        <main ref={mainRef} style={{ flex: 1, overflow: 'auto', padding: 24, background: t.bg, color: t.text }}>
          <ErrorBoundary key={state.view}>
            <Suspense fallback={<PageSpinner />}>
              {renderView()}
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      {state.searchOpen && !isCustomer && <SearchOverlay />}
      {!isCustomer && <TaskDrawer />}
      <Toast />
      {!isCustomer && <AnalystRequestFab />}
    </div>
  );
}
